import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Loader2, RefreshCw } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import HistoryMobileCard from "./HistoryMobileCard";
import HistoryPagination, { HISTORY_PAGE_SIZE } from "./HistoryPagination";

const currency = (value) =>
  typeof value === "number"
    ? `${value.toLocaleString("fr-FR")} F`
    : "—";

const discountValueLabel = (row) => {
  const value = Number(row?.discount_value);
  if (!Number.isFinite(value)) return "—";
  if (row?.discount_unit === "percentage") return `${value}%`;
  return currency(value);
};

const typeLabel = (row) => (row?.discount_scope === "item" ? "Article" : "Panier");

const DiscountHistory = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const adminBasePath = location.pathname.startsWith("/superadmin")
    ? "/superadmin"
    : "/admin";

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch("/api/discount-logs");
      if (!res.ok) throw new Error("Impossible de charger les remises");
      const data = await res.json();
      setLogs(Array.isArray(data) ? data : []);
      setCurrentPage(1);
    } catch (err) {
      setError(err.message || "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const rows = useMemo(() => {
    return (logs || []).map((log) => ({
      id: log.id,
      time: log.created_at,
      orderNumber: log.order_number || "—",
      author: log.user_name || "—",
      reason: log.reason || "—",
      type: typeLabel(log),
      normal: typeof log.normal_amount === "number" ? log.normal_amount : Number(log.normal_amount),
      reduction: discountValueLabel(log),
    }));
  }, [logs]);
  const totalPages = Math.max(1, Math.ceil(rows.length / HISTORY_PAGE_SIZE));
  const pageRows = rows.slice(
    (Math.min(currentPage, totalPages) - 1) * HISTORY_PAGE_SIZE,
    Math.min(currentPage, totalPages) * HISTORY_PAGE_SIZE,
  );

  return (
    <div className="w-full min-w-0 max-w-7xl mx-auto px-0 sm:px-4 py-4 sm:py-8 space-y-5">
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-start gap-3 sm:flex sm:items-center">
        <button
          type="button"
          aria-label="Retour aux permissions"
          onClick={() => navigate(`${adminBasePath}/permissions`)}
          className="p-2 rounded-2xl border border-white/10 bg-white/60 text-slate-700 hover:bg-white shadow-sm dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="min-w-0">
          <p className="text-xs uppercase tracking-[0.25em] text-amber-400">
            Permissions
          </p>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 dark:text-white">
            Historique des remises
          </h1>
          <p className="text-sm text-slate-500 dark:text-white/60">
            Remises appliquées sur panier ou article (traçabilité).
          </p>
        </div>
        <div className="col-span-2 sm:ml-auto sm:shrink-0">
          <button
            type="button"
            onClick={loadLogs}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl text-sm font-semibold border border-white/10 bg-white/60 hover:bg-white dark:bg-white/5 dark:text-white dark:border-white/10 dark:hover:bg-white/10"
          >
            <RefreshCw className="w-4 h-4" />
            Rafraîchir
          </button>
        </div>
      </div>

      <div className="rounded-3xl border border-white/10 bg-white/80 dark:bg-[#0b1328] dark:border-white/10 shadow-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-white/10 dark:border-white/5 flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700 dark:text-white">
            {rows.length} remise(s)
          </span>
        </div>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-slate-500 dark:text-white/60 gap-2">
            <Loader2 className="w-5 h-5 animate-spin" />
            Chargement...
          </div>
        ) : error ? (
          <div className="py-8 text-center text-red-500 text-sm">{error}</div>
        ) : rows.length === 0 ? (
          <div className="py-8 text-center text-slate-500 dark:text-white/60 text-sm">
            Aucune remise enregistrée.
          </div>
        ) : (
          <>
          <div className="grid min-w-0 gap-3 p-3 lg:hidden">
            {pageRows.map((row) => (
              <HistoryMobileCard
                key={row.id}
                title={`Commande ${row.orderNumber}`}
                time={row.time}
                badge={`Remise : ${row.reduction}`}
                author={row.author}
                reason={row.reason}
                fields={[["Type de remise", row.type], ["Prix normal", currency(row.normal)]]}
              />
            ))}
          </div>
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left bg-white/50 dark:bg-white/5 border-b border-white/10">
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    Heure
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    N° commande
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    Auteur
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    Motif
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70">
                    Type
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70 text-right">
                    Prix normal
                  </th>
                  <th className="px-4 py-3 font-semibold text-slate-600 dark:text-white/70 text-right">
                    Réduction
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {pageRows.map((row) => (
                  <tr
                    key={row.id}
                    className="hover:bg-white/60 dark:hover:bg-white/5 transition"
                  >
                    <td className="px-4 py-3 text-slate-700 dark:text-white/80">
                      {row.time
                        ? new Date(row.time).toLocaleString("fr-FR")
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-slate-800 dark:text-white font-semibold">
                      {row.orderNumber}
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-white/80">
                      {row.author}
                    </td>
                    <td className="px-4 py-3 text-slate-600 dark:text-white/70">
                      {row.reason}
                    </td>
                    <td className="px-4 py-3 text-slate-700 dark:text-white/80">
                      {row.type}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-800 dark:text-white font-semibold">
                      {currency(row.normal)}
                    </td>
                    <td className="px-4 py-3 text-right text-emerald-600 dark:text-emerald-300 font-semibold">
                      {row.reduction}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <HistoryPagination
            currentPage={Math.min(currentPage, totalPages)}
            totalItems={rows.length}
            onPageChange={setCurrentPage}
          />
          </>
        )}
      </div>
    </div>
  );
};

export default DiscountHistory;
